import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Database } from '@jac/shared/generated/db-types'
import { supabase } from '@/lib/supabase'
import { createRealtimeReloadScheduler } from '@/lib/realtimeReload'
import { trackQuery } from '@/lib/perf'
import { createLoadGuard } from '@/lib/loadGuard'
import type { Priority } from '@/components/ui/PriorityBadge'

type IngestionSource = Database['public']['Enums']['ingestion_source']
type BankAccessStatus = Database['public']['Enums']['bank_access_status']

export type ClientDashboardRow = {
  client_id: string
  nif: string
  name: string
  carteira_id: string
  received: number
  identified: number
  submitted: number
  errors: number
  /** Per-client document total for the selected Período, scoped by ISSUE DATE
   *  over the same universe as /documentos "Todos" (non-dispensado, archived
   *  included) — reconciles the two views. `received` is the created_at
   *  "arrived this period" count. From f_client_dashboard_stats. */
  total_in_period: number
  has_bank_statement: boolean
  bank_access_status: BankAccessStatus
  primary_channel: IngestionSource | null
  last_received_at: string | null
  priority: Priority
}

export type DashboardKpis = {
  totalClients: number
  openClients: number
  urgentClients: number
  inProgressClients: number
  totalReceived: number
  totalIdentified: number
  totalSubmitted: number
  totalErrors: number
  totalPending: number
}

export type YearMonth = { year: number; month: number }
export type DateRange = { from: Date; to: Date }

function currentYearMonth(now: Date = new Date()): YearMonth {
  return { year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 }
}

// Turn a YearMonth into a [from, to) half-open interval — the convention
// the new f_client_dashboard_stats(timestamptz, timestamptz) overload uses.
export function yearMonthToRange({ year, month }: YearMonth): DateRange {
  return {
    from: new Date(Date.UTC(year, month - 1, 1)),
    to: new Date(Date.UTC(year, month, 1)),
  }
}

export function currentMonthRange(now: Date = new Date()): DateRange {
  return yearMonthToRange(currentYearMonth(now))
}

// A client is "open" when it has unfinished work:
//   - at least one error, OR
//   - fewer submitted than received, OR
//   - missing bank statement AND any docs received (signal that they should
//     have sent one).
// Urgent = has errors OR missing bank statement while close to deadline.
// "Urgent" thresholding based on deadline is a 1c.1 simplification — 1c.3
// will plug in the real notification engine.
export function computeKpis(rows: ClientDashboardRow[]): DashboardKpis {
  const totalClients = rows.length
  let openClients = 0
  let urgentClients = 0
  let inProgressClients = 0
  let totalReceived = 0
  let totalIdentified = 0
  let totalSubmitted = 0
  let totalErrors = 0

  for (const r of rows) {
    totalReceived += r.received
    totalIdentified += r.identified
    totalSubmitted += r.submitted
    totalErrors += r.errors

    const hasWork =
      r.errors > 0 || r.submitted < r.received || (r.received > 0 && !r.has_bank_statement)
    if (hasWork) openClients += 1

    const isUrgent = r.errors > 0 || (r.received > 0 && !r.has_bank_statement)
    if (isUrgent) urgentClients += 1
    else if (hasWork) inProgressClients += 1
  }
  const totalPending = Math.max(0, totalReceived - totalIdentified)
  return {
    totalClients,
    openClients,
    urgentClients,
    inProgressClients,
    totalReceived,
    totalIdentified,
    totalSubmitted,
    totalErrors,
    totalPending,
  }
}

export function useOperationsDashboard(rangeOrPeriod?: DateRange | YearMonth) {
  // Accept either a YearMonth (legacy callers) or a full DateRange (new
  // /operacoes/clientes filter chip). Default = current month half-open.
  const range = useMemo<DateRange>(() => {
    if (!rangeOrPeriod) return currentMonthRange()
    if ('from' in rangeOrPeriod) return rangeOrPeriod
    return yearMonthToRange(rangeOrPeriod)
  }, [rangeOrPeriod])
  const [rows, setRows] = useState<ClientDashboardRow[]>([])
  const [loading, setLoading] = useState(true)
  const [refetching, setRefetching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasInitiallyLoadedRef = useRef(false)
  // Last-write-wins guard (see useClientDetail): the realtime 800ms-debounced reload can interleave
  // with a Período change, so an older f_client_dashboard_stats response for the previous range could
  // land last and populate the table for the wrong period. Drop any superseded response.
  const guardRef = useRef(createLoadGuard())

  const load = useCallback(async () => {
    const myGen = guardRef.current.begin()
    if (hasInitiallyLoadedRef.current) {
      setRefetching(true)
    } else {
      setLoading(true)
    }
    // RPC against the new range-based overload. Half-open interval —
    // [from, to) — so callers can pass first-of-next-month for the upper
    // bound without double-counting.
    const { data, error: loadErr } = await trackQuery(
      'useOperationsDashboard.f_client_dashboard_stats',
      async () =>
        await supabase.rpc('f_client_dashboard_stats', {
          p_from: range.from.toISOString(),
          p_to: range.to.toISOString(),
        }),
      { from: range.from.toISOString(), to: range.to.toISOString() },
    )
    // Superseded by a newer load() (Período changed / realtime reload) — drop this stale result.
    if (!guardRef.current.isCurrent(myGen)) return
    if (loadErr) {
      setError(loadErr.message)
      setRows([])
    } else {
      setError(null)
      setRows((data as ClientDashboardRow[]) ?? [])
    }
    setLoading(false)
    setRefetching(false)
    hasInitiallyLoadedRef.current = true
  }, [range])

  useEffect(() => {
    load()
    // Realtime: any change on documents or clients invalidates the
    // aggregated view; we just refetch — through the shared 30s scheduler
    // (hidden tabs never reload, focus flushes) so a bulk upload can't turn
    // every open tab into an aggregate-query loop (2026-09-09 incident).
    const scheduler = createRealtimeReloadScheduler(() => void load())
    const channel = supabase
      .channel('operations-dashboard')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'documents' }, scheduler.poke)
      // `clients` NÃO: realtime morto por desenho desde 20260622140000 (sem grant de SELECT
      // table-level não há autorização de subscrição). O poke que interessa já vem de
      // `documents`, co-subscrita acima e ligada ao mesmo load().
      .subscribe()
    return () => {
      scheduler.dispose()
      supabase.removeChannel(channel)
    }
  }, [load])

  // Memoize so consumers (VisaoGeral, MinhaCarteira) don't re-run the
  // O(N) KPI fold on every unrelated state change (search input, filter
  // dropdown click, etc.). rows only changes on load() or realtime poke.
  const kpis = useMemo(() => computeKpis(rows), [rows])

  return {
    rows,
    kpis,
    loading,
    refetching,
    error,
    reload: load,
  }
}

export function useDashboardDistinctMonths(): {
  months: YearMonth[]
  loading: boolean
} {
  const [months, setMonths] = useState<YearMonth[]>([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let cancelled = false
    void supabase.rpc('f_dashboard_distinct_months').then(({ data }) => {
      if (cancelled) return
      const parsed = (data ?? []).map((r) => ({ year: r.year, month: r.month }))
      // Always include current month so the user can switch back even if no
      // docs arrived this month yet.
      const current = currentYearMonth()
      if (!parsed.some((m) => m.year === current.year && m.month === current.month)) {
        parsed.unshift(current)
      }
      setMonths(parsed)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return { months, loading }
}
